/**
 * SDCX 改键矩阵：4 字节单元 [Type, Code1, Code2, Code3]
 * Type=0x20 普通键/修饰键；Type=0x13 禁用；Type=0x1f 自定义功能；
 * Type=0x30 多媒体；Type=0x51 Profile；Type=0x60 宏
 */

import {
  buildSdcxMacroMatrixCell,
  resolveSdcxConsumerUiKeyCodeFromCell,
  SDCX_CONSUMER_TYPE,
  SDCX_MACRO_TYPE,
  SDCX_MACRO_UI_BASE,
  SDCX_MACRO_UI_SLOT_COUNT,
  SDCX_PROFILE_TYPE,
  SDCX_UI_TO_CONSUMER_CELL,
  SDCX_UI_TO_PROFILE_CODE1,
  SDCX_PROFILE_CODE1_TO_UI,
} from "./sdcxExtraKeys";
import {
  resolveSdcxFnFuncDisplayKeyCode,
  SDCX_FN_UI_KEYCODE,
  SDCX_UI_ICON_TO_FUNC,
} from "./sdcxFnDisplay";

export const SDCX_KB_TYPE = 0x20;
export const SDCX_DISABLED_TYPE = 0x13;
export const SDCX_FUNC_TYPE = 0x1f;

export {
  SDCX_CONSUMER_TYPE,
  SDCX_MACRO_TYPE,
  SDCX_PROFILE_TYPE,
  SDCX_MACRO_UI_BASE,
  SDCX_MACRO_UI_SLOT_COUNT,
  buildSdcxMacroMatrixCell,
} from "./sdcxExtraKeys";

export { SDCX_FN_UI_KEYCODE };

export type SdcxMatrixCell = {
  type: number;
  code1: number;
  code2: number;
  code3: number;
};

export type SdcxMatrixCellWrite = SdcxMatrixCell;

/** UI HID 修饰键 → 协议热键位（Code1，Code2=0） */
const UI_HID_TO_SDCX_HOTKEY: Readonly<Record<number, number>> = {
  224: 0x01,
  225: 0x02,
  226: 0x04,
  227: 0x08,
  228: 0x10,
  229: 0x20,
  230: 0x40,
  231: 0x80,
};

/** 协议热键位 → UI HID 修饰键 */
const SDCX_HOTKEY_TO_UI_HID: Readonly<Record<number, number>> = {
  0x01: 224,
  0x02: 225,
  0x04: 226,
  0x08: 227,
  0x10: 228,
  0x20: 229,
  0x40: 230,
  0x80: 231,
};

/** UI 普通键 → USB HID usage（标准 HID keyboard/keypad 页） */
export const SDCX_UI_TO_HID_USAGE: Readonly<Record<number, number>> = {
  41: 0x29,
  30: 0x1e,
  31: 0x1f,
  32: 0x20,
  33: 0x21,
  34: 0x22,
  35: 0x23,
  36: 0x24,
  37: 0x25,
  38: 0x26,
  39: 0x27,
  45: 0x2d,
  46: 0x2e,
  53: 0x35,
  42: 0x35,
  58: 0x3a,
  59: 0x3b,
  60: 0x3c,
  43: 0x2b,
  20: 0x14,
  26: 0x1a,
  8: 0x08,
  21: 0x15,
  23: 0x17,
  28: 0x1c,
  24: 0x18,
  12: 0x0c,
  18: 0x12,
  19: 0x13,
  47: 0x2f,
  48: 0x30,
  49: 0x31,
  57: 0x39,
  4: 0x04,
  22: 0x16,
  7: 0x07,
  9: 0x09,
  10: 0x0a,
  11: 0x0b,
  13: 0x0d,
  14: 0x0e,
  15: 0x0f,
  51: 0x33,
  52: 0x34,
  40: 0x28,
  29: 0x1d,
  27: 0x1b,
  6: 0x06,
  25: 0x19,
  5: 0x05,
  17: 0x11,
  16: 0x10,
  54: 0x36,
  55: 0x37,
  56: 0x38,
  82: 0x52,
  44: 0x2c,
  80: 0x50,
  81: 0x51,
  79: 0x4f,
  76: 0x4c,
  77: 0x4d,
  78: 0x4e,
  75: 0x4b,
  61: 0x3d,
  62: 0x3e,
  63: 0x3f,
  64: 0x40,
  65: 0x41,
  66: 0x42,
  67: 0x43,
  68: 0x44,
  69: 0x45,
  70: 0x46,
  71: 0x47,
  72: 0x48,
  73: 0x49,
  74: 0x4a,
  89: 0x59,
  90: 0x5a,
  91: 0x5b,
};

const HID_USAGE_TO_UI: Readonly<Record<number, number>> = {
  ...Object.fromEntries(
    Object.entries(SDCX_UI_TO_HID_USAGE).map(([ui, hid]) => [hid, Number(ui)])
  ),
  /** 公版 DK63 出厂矩阵 Backspace 使用 0x35，改键/部分固件回 0x2a */
  0x35: 42,
  0x2a: 42,
};

export function parseSdcxMatrixCell(
  matrix: Uint8Array,
  index: number
): SdcxMatrixCell {
  const base = index * 4;
  return {
    type: matrix[base] ?? 0,
    code1: matrix[base + 1] ?? 0,
    code2: matrix[base + 2] ?? 0,
    code3: matrix[base + 3] ?? 0,
  };
}

export function writeSdcxMatrixCell(
  matrix: Uint8Array,
  index: number,
  cell: SdcxMatrixCellWrite
): void {
  const base = index * 4;
  matrix[base] = cell.type & 0xff;
  matrix[base + 1] = cell.code1 & 0xff;
  matrix[base + 2] = cell.code2 & 0xff;
  matrix[base + 3] = cell.code3 & 0xff;
}

/** 矩阵格是否有有效键定义 */
export function isSdcxMatrixCellOccupied(cell: SdcxMatrixCell): boolean {
  if (cell.type === SDCX_DISABLED_TYPE) return true;
  if (cell.type === SDCX_FUNC_TYPE) return cell.code1 !== 0;
  if (cell.type === SDCX_CONSUMER_TYPE) {
    return cell.code1 !== 0 || cell.code2 !== 0;
  }
  if (cell.type === SDCX_MACRO_TYPE) return true;
  if (cell.type === SDCX_PROFILE_TYPE) return cell.code1 !== 0;
  if (cell.type === SDCX_KB_TYPE) {
    return cell.code1 !== 0 || cell.code2 !== 0;
  }
  return cell.type !== 0;
}

/** 矩阵索引级 HID 反查（同一 HID 在不同键位语义不同） */
const SDCX_MATRIX_INDEX_HID_TO_UI: Readonly<Record<number, number>> = {
  /** Fn 层 `~ */
  0: 53,
  /** 默认层 Backspace（公版用 0x35 而非 0x2a） */
  13: 42,
};

/** 协议矩阵单元 → UI 键码 */
export function sdcxMatrixCellToUiKeyCode(
  cell: SdcxMatrixCell,
  matrixIndex?: number
): number {
  if (cell.type === SDCX_DISABLED_TYPE) return 0;
  if (cell.type === SDCX_FUNC_TYPE && cell.code1 !== 0) {
    return resolveSdcxFnFuncDisplayKeyCode(cell.code1);
  }
  if (cell.type === SDCX_CONSUMER_TYPE) {
    return resolveSdcxConsumerUiKeyCodeFromCell(cell.code1, cell.code2);
  }
  if (cell.type === SDCX_MACRO_TYPE) {
    const slot = cell.code1;
    if (slot >= 0 && slot < SDCX_MACRO_UI_SLOT_COUNT) {
      return SDCX_MACRO_UI_BASE + slot;
    }
  }
  if (cell.type === SDCX_PROFILE_TYPE && cell.code1 !== 0) {
    return SDCX_PROFILE_CODE1_TO_UI[cell.code1] ?? 0;
  }
  if (cell.type === SDCX_KB_TYPE) {
    if (!cell.code2 && cell.code1) {
      return SDCX_HOTKEY_TO_UI_HID[cell.code1] ?? 0;
    }
    if (cell.code2) {
      if (matrixIndex != null) {
        const byIndex = SDCX_MATRIX_INDEX_HID_TO_UI[matrixIndex];
        if (byIndex != null && (cell.code2 === 0x35 || cell.code2 === 0x2a)) {
          return byIndex;
        }
      }
      return HID_USAGE_TO_UI[cell.code2] ?? 0;
    }
  }
  return 0;
}

/** UI 键码 → 协议矩阵单元 */
export function uiKeyCodeToSdcxMatrixCell(
  keyCode: number
): SdcxMatrixCellWrite | null {
  if (!Number.isFinite(keyCode) || keyCode <= 0) return null;
  if (keyCode === SDCX_FN_UI_KEYCODE) {
    return { type: SDCX_FUNC_TYPE, code1: 0x01, code2: 0, code3: 0 };
  }
  const funcCode = SDCX_UI_ICON_TO_FUNC[keyCode];
  if (funcCode != null) {
    return { type: SDCX_FUNC_TYPE, code1: funcCode, code2: 0, code3: 0 };
  }
  if (
    keyCode >= SDCX_MACRO_UI_BASE &&
    keyCode < SDCX_MACRO_UI_BASE + SDCX_MACRO_UI_SLOT_COUNT
  ) {
    return buildSdcxMacroMatrixCell(keyCode - SDCX_MACRO_UI_BASE);
  }
  const consumerCell = SDCX_UI_TO_CONSUMER_CELL[keyCode];
  if (consumerCell != null) {
    return {
      type: SDCX_CONSUMER_TYPE,
      code1: consumerCell[0],
      code2: consumerCell[1],
      code3: 0,
    };
  }
  const profileCode = SDCX_UI_TO_PROFILE_CODE1[keyCode];
  if (profileCode != null) {
    return {
      type: SDCX_PROFILE_TYPE,
      code1: profileCode,
      code2: 0,
      code3: 0,
    };
  }
  const hotkey = UI_HID_TO_SDCX_HOTKEY[keyCode];
  if (hotkey != null) {
    return { type: SDCX_KB_TYPE, code1: hotkey, code2: 0, code3: 0 };
  }
  const hid = SDCX_UI_TO_HID_USAGE[keyCode];
  if (hid != null) {
    return { type: SDCX_KB_TYPE, code1: 0, code2: hid, code3: 0 };
  }
  if (keyCode <= 0xff) {
    return { type: SDCX_KB_TYPE, code1: 0, code2: keyCode, code3: 0 };
  }
  return null;
}
